import { StyleView } from './types';

const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE']);

/**
 * Picks the element that holds the screen's own content. Shell layouts mark their routed
 * content with `data-screen-root`; when markers nest, the innermost one wins so the order
 * sidebar, tabs and information bar stay outside by construction.
 */
export const selectContentRoot = (doc: Document): Element => {
  const roots = Array.from(doc.querySelectorAll('[data-screen-root]'));
  if (roots.length === 0) return doc.querySelector('main') ?? doc.body;

  const innermost = roots.filter((root) => root.querySelector('[data-screen-root]') === null);
  if (innermost.length === 1) return innermost[0];
  return roots[roots.length - 1];
};

/**
 * Self-only visibility check. Ancestors are deliberately not inspected: MUI's ModalManager sets
 * `aria-hidden="true"` on `#root` while a dialog is open, which would otherwise hide everything.
 */
export const isHidden = (el: Element, view: StyleView): boolean => {
  if (el.hasAttribute('hidden')) return true;
  if (el.getAttribute('aria-hidden') === 'true') return true;
  if (el.classList.contains('MuiCollapse-hidden')) return true;
  if (!view) return false;

  const style = view.getComputedStyle(el);
  return style.display === 'none' || style.visibility === 'hidden';
};

// The budget check runs before `isHidden` so an oversized screen stops costing style lookups.
export const isSkipped = (el: Element, view: StyleView): boolean => (
  SKIPPED_TAGS.has(el.tagName)
  || el.hasAttribute('data-screen-exclude')
  || view?.isOverBudget?.(el) === true
  || isHidden(el, view)
);

/** Depth-first, read-only walk over the live DOM that never enters hidden or excluded subtrees. */
export const walkVisible = (root: Element, view: StyleView, visit: (el: Element) => void): void => {
  if (isSkipped(root, view)) return;

  visit(root);
  Array.from(root.children).forEach((child) => walkVisible(child, view, visit));
};
